// OverlayModal.js - This file defines the OverlayModal component, which serves as a generic modal container. It is responsible for:
// Displaying the Modal: It controls the visibility and the basic styling of the modal overlay that appears over your main application content.
// Managing Close Functionality: It includes a close button and the logic to hide the modal when the button is clicked.
// Hosting Child Components: The component is designed to receive child components as props, allowing it to display different content dynamically. This is done via the children prop, which can render anything passed to it, such as forms, tabs, or information panels.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './OverlayModal.css';
import SupplySourcesTab from './SupplySourcesTab';
import SupplyMatchingSummaryTab from './SupplyMatchingSummaryTab';
import RFQEmailsTab from './RFQEmailsTab';

const OverlayModal = ({ // Accept props
    isVisible, 
    onClose, 
    selectedSupplySources,
    setSelectedSupplySources,
    supplySourceFiles,
    setSupplySourceFiles,
    fetchSupplySourceFiles, 
    handleSupplyCheckboxChange,
    handleSupplySourcesClick, 
    summaryData,  // New prop we added
    setIsWaitingForResponse, 
    isWaitingForResponse,  
    handleSendClickButton, 
    setHandleSendClickButton,
    selectedDocuments,
    activeTab,
    setActiveTab,
    inputText,
    fullMatches,
    formHasValues,
}) => {
    console.log("OverlayModal received formHasValues:", formHasValues);
    
    // Memoized initial states
    const memoizedStates = useMemo(() => ({
        isDeletingAll: false,
        deletedEmailIndices: [],
        deletingEmails: []
    }), []);
    
    const [rfqEmailData, setRfqEmailData] = useState([]);

    // Only run effects when necessary
    useEffect(() => {
        if (isVisible && activeTab === 'supplySources' && !supplySourceFiles) {
            fetchSupplySourceFiles();
        }
    }, [isVisible, activeTab, supplySourceFiles, fetchSupplySourceFiles]);

    // Memoized UI Components
    const TabButtons = useMemo(() => (
        <ul className="modal-tabs">
            <li 
                className={activeTab === 'supplySources' ? 'active' : ''} 
                onClick={() => setActiveTab('supplySources')}
            >
                Supply Sources
            </li>
            <li 
                className={activeTab === 'supplyMatchingSummary' ? 'active' : ''} 
                onClick={() => setActiveTab('supplyMatchingSummary')}
            >
                Supply Matching Summary
            </li>
            <li 
                className={activeTab === 'rfqEmails' ? 'active' : ''} 
                onClick={() => setActiveTab('rfqEmails')}
            >
                RFQ Emails
            </li>
        </ul>
    ), [activeTab, setActiveTab]);

    const SupplySourcesContent = useMemo(() => {
        console.log("SupplySourcesContent memo running with formHasValues:", formHasValues);
        return (
            activeTab === 'supplySources' ? (
                <div className="supply-sources-content">
                    <SupplySourcesTab
                        supplySourceFiles={supplySourceFiles}
                        setSupplySourceFiles={setSupplySourceFiles} 
                        handleSupplyCheckboxChange={handleSupplyCheckboxChange}
                        selectedSupplySources={selectedSupplySources}
                        setSelectedSupplySources={setSelectedSupplySources} 
                        handleSendClickButton={handleSendClickButton} 
                        setHandleSendClickButton={setHandleSendClickButton}
                        handleModalClose={onClose}
                        selectedDocuments={selectedDocuments}
                        fetchSupplySourceFiles={fetchSupplySourceFiles} 
                        handleSupplySourcesClick={handleSupplySourcesClick} 
                        inputText={inputText}                          
                        formHasValues={formHasValues}
                    />
                    {console.log("OverlayModal formHasValues:", formHasValues)}
                </div>
            ) : null
        );
    }, [
        activeTab,
        supplySourceFiles,
        setSupplySourceFiles,
        handleSupplyCheckboxChange,
        selectedSupplySources,
        setSelectedSupplySources,
        handleSendClickButton,
        setHandleSendClickButton,
        onClose,
        selectedDocuments,
        fetchSupplySourceFiles,
        handleSupplySourcesClick,
        inputText,
        formHasValues
    ]);

    console.log("OverlayModal props:", { summaryData, fullMatches });

    const SupplyMatchingSummaryContent = useMemo(() => (
        activeTab === 'supplyMatchingSummary' ? (
            <SupplyMatchingSummaryTab
                summaryData={summaryData?.summary || []} // Extract summary
                fullMatches={summaryData?.fullMatches || []} // Extract fullMatches
                setRfqEmailData={setRfqEmailData}
                setIsWaitingForResponse={setIsWaitingForResponse}
                isWaitingForResponse={isWaitingForResponse}
                setActiveTab={setActiveTab}
            />
        ) : null
    ), [
        activeTab,
        summaryData,
        //fullMatches, Since summaryData now contains both summary and fullMatches, you do not need fullMatches in the dependency array. You are already using summaryData as the source of truth.
        setIsWaitingForResponse,
        isWaitingForResponse,
        setActiveTab
    ]);

    const RFQEmailsContent = useMemo(() => (
        activeTab === 'rfqEmails' ? (
            <RFQEmailsTab
                rfqEmailData={rfqEmailData}
                setRfqEmailData={setRfqEmailData}
            />
        ) : null
    ), [activeTab, rfqEmailData, setRfqEmailData]);

    if (!isVisible) return null;
 
    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    {TabButtons}
                    <button type="button" className="close-button" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    {SupplySourcesContent}
                    {SupplyMatchingSummaryContent}
                    {RFQEmailsContent}
                </div>
            </div>
        </div>
    );
};

export default React.memo(OverlayModal);